import React from 'react';
import { Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUpcomingAnniversaries } from '../../hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { UpcomingAnniversary } from '../../types';

interface UpcomingAnniversariesCardProps {
  days?: number;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function ProximityBadge({ days, t }: { days: number; t: (key: string, options?: Record<string, unknown>) => string }) {
  if (days === 0) {
    return (
      <Badge variant="success" className="whitespace-nowrap">
        {t('anniversaries.today')}
      </Badge>
    );
  }
  if (days === 1) {
    return (
      <Badge variant="warning" className="whitespace-nowrap">
        {t('anniversaries.tomorrow')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="whitespace-nowrap">
      {t('anniversaries.inDays', { count: days })}
    </Badge>
  );
}

function AnniversaryRow({
  anniversary,
  t,
  tCommon,
}: {
  anniversary: UpcomingAnniversary;
  t: (key: string, options?: Record<string, unknown>) => string;
  tCommon: (key: string) => string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8">
        {anniversary.photoUrl && (
          <AvatarImage src={anniversary.photoUrl} alt={anniversary.displayName} />
        )}
        <AvatarFallback className="text-xs">
          {getInitials(anniversary.displayName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{anniversary.displayName}</p>
        <p className="text-xs text-muted-foreground">
          {tCommon(`months.${anniversary.hireMonth}`)} {anniversary.hireDay}
        </p>
      </div>
      <Badge variant="secondary" className="whitespace-nowrap">
        {t('anniversaries.years', { count: anniversary.yearsOfService })}
      </Badge>
      <ProximityBadge days={anniversary.daysUntilAnniversary} t={t} />
    </div>
  );
}

export const UpcomingAnniversariesCard: React.FC<UpcomingAnniversariesCardProps> = ({ days = 30 }) => {
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { data: anniversaries = [], isLoading } = useUpcomingAnniversaries(days);

  if (!isLoading && anniversaries.length === 0) {
    return null;
  }

  return (
    <Card data-testid="upcoming-anniversaries-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          {t('anniversaries.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground">{tCommon('loading')}</div>
        ) : (
          <div className="space-y-3">
            {anniversaries.map((a) => (
              <AnniversaryRow key={a.id} anniversary={a} t={t} tCommon={tCommon} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
